import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect } from 'react';
import { AlertContext } from '../contexts/alert-context';
import { companionStatusChangedPayloadSchema } from '../data/receiving-ws';
import { isTerminalStatus, type Upload } from '../data/upload';
import type { UploaderConnection } from '../data/uploader-connection';
import { companionConnectionsQueryOptions, uploadsQueryOptions } from '../query/query-options';
import { storeLayout } from '../route-apis';
import { useStoreWebSocket } from '../websocket/use-store-websocket';
import { useActive } from './use-active';

export function useUploads() {
  const { addErrorAlert } = useContext(AlertContext);
  const { store, uploadService } = storeLayout.useRouteContext();
  const active = useActive();

  const { data: uploads = [], isLoading } = useQuery({
    ...uploadsQueryOptions(store.id),
    queryFn: () => {
      return uploadService.getUploadsByStore(store.id).catch((error: Error) => {
        addErrorAlert(error.message, { cause: error });
        throw error;
      });
    },
    // Poll while an upload is in flight, but only when the user is actually
    // here — an idle or hidden tab left open overnight should not keep polling.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (active && data?.some((u) => !isTerminalStatus(u.status))) {
        return 5_000;
      }

      return false;
    },
  });

  return {
    uploads,
    loading: uploads.length === 0 && isLoading,
  };
}

export function useCompanionConnections(): {
  connections: UploaderConnection[];
  loading: boolean;
} {
  const { addErrorAlert } = useContext(AlertContext);
  const { store, uploadService } = storeLayout.useRouteContext();
  const active = useActive();
  const queryClient = useQueryClient();
  const { subscribe } = useStoreWebSocket();

  const { data: connections = [], isLoading } = useQuery({
    ...companionConnectionsQueryOptions(store.id),
    queryFn: () => {
      return uploadService.getCompanionConnections(store.id).catch((error: Error) => {
        addErrorAlert(error.message, { cause: error });
        throw error;
      });
    },
    // WebSocket handles real-time updates; keep a slow fallback for reconnect
    // gaps, but only while the user is actually here.
    refetchInterval: active ? 5 * 60_000 : false,
  });

  useEffect(() => {
    return subscribe('companion_status_changed', companionStatusChangedPayloadSchema, () => {
      queryClient.invalidateQueries({
        queryKey: companionConnectionsQueryOptions(store.id).queryKey,
      });
    });
  }, [subscribe, queryClient, store.id]);

  return {
    connections,
    loading: isLoading,
  };
}

export function useTriggerUpload() {
  const { addErrorAlert } = useContext(AlertContext);
  const { store, uploadService } = storeLayout.useRouteContext();
  const queryClient = useQueryClient();

  // `mutate`, not `mutateAsync`: the mutationFn below alerts and rethrows so
  // React Query marks the mutation failed, and the only caller fires this from
  // an onClick without awaiting it. `mutateAsync` would hand that rejection to
  // the window as an unhandled rejection, which Datadog logged as a crash.
  //
  // That was one of two reports for the same expected 409. The other came from
  // the fetch wrapper, and uploadService.triggerUpload now opts out of it with
  // `report: false` — so the toast below is the whole story.
  const { mutate: triggerUpload, isPending } = useMutation({
    mutationFn: () => {
      return uploadService.triggerUpload(store.id).catch((error: Error) => {
        // The API says which companion is busy; the fetch wrapper prefixes
        // thrown errors with "Invalid response:" and Go error strings are
        // lowercase, so strip and capitalize for the toast.
        const reason = error.message.replace(/^Invalid response:\s*/, '');
        addErrorAlert(
          reason
            ? reason.charAt(0).toUpperCase() + reason.slice(1)
            : 'Could not trigger upload. Please try again.',
          { cause: error }
        );
        throw error;
      });
    },
    onSuccess: (newUpload: Upload) => {
      queryClient.setQueryData(['stores', store.id, 'uploads'], (oldUploads: Upload[] = []) => [
        newUpload,
        ...oldUploads,
      ]);
    },
  });

  return { triggerUpload, isPending };
}
